import { type ReactNode, useMemo, useState } from "react";
import {
    RefreshControl,
    ScrollView,
    type ScrollViewProps,
    StyleSheet,
    useWindowDimensions,
    View,
    type ViewProps,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { AppVersion } from "~/components/feedback/AppVersion";
import { BackgroundGradient } from "~/components/layout/BackgroundGradient";
import { FooterSpacer } from "~/components/layout/FooterSpacer";
import { HeaderSpacer } from "~/components/layout/HeaderSpacer";

import { Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { setHeightOnLayout } from "~/util/layout";

type ScrollablePageProps = {
    scrollable?: true;
} & Partial<ScrollViewProps>;

type StaticPageProps = {
    scrollable: false;
} & Partial<ViewProps>;

type PageProps = {
    gradient?: boolean;
    spacer?: boolean;
    tabs?: boolean;
    padding?: number;
    refreshWith?: () => Promise<unknown>;
    isRefreshing?: boolean;
    children: ReactNode;
    scrollable?: boolean;
    gap?: number;
    showVersion?: boolean;
} & (ScrollablePageProps | StaticPageProps);

export const Page = ({
    gradient,
    spacer,
    tabs,
    refreshWith,
    isRefreshing,
    children,
    showVersion,
    scrollable = true,
    padding = Space.md,
    gap = Space.lg,
    ...containerProps
}: PageProps) => {
    const { background } = useTheme();
    const [refreshing, setRefreshing] = useState(false);
    const currently_refreshing = isRefreshing || refreshing;
    const { height, width } = useWindowDimensions();
    const [minHeight, setMinHeight] = useState(height);
    const onLayout = setHeightOnLayout(setMinHeight, containerProps.onLayout);

    const refresh_control = useMemo(() => {
        if (!refreshWith) {
            return undefined;
        }
        const doRefresh = () => {
            if (currently_refreshing) {
                return;
            }

            setRefreshing(true);

            refreshWith().finally(() => {
                setRefreshing(false);
            });
        };

        return <RefreshControl refreshing={currently_refreshing} onRefresh={doRefresh} />;
    }, [refreshWith, currently_refreshing]);

    return (
        <View style={[styles.wrapper, background.secondary, { height, width }]}>
            {gradient && <BackgroundGradient />}
            <SafeAreaView style={styles.container}>
                {spacer && <HeaderSpacer />}
                {scrollable ? (
                    <ScrollView
                        {...containerProps}
                        onLayout={onLayout}
                        style={styles.container}
                        contentContainerStyle={[styles.body, { gap, padding, minHeight }, containerProps.style]}
                        refreshControl={refresh_control}
                    >
                        {children}
                        {showVersion && <AppVersion />}
                    </ScrollView>
                ) : (
                    <View
                        {...containerProps}
                        style={[styles.container, styles.body, { gap, padding }, containerProps.style]}
                    >
                        {children}
                        {showVersion && <AppVersion />}
                    </View>
                )}
                {tabs && <FooterSpacer />}
            </SafeAreaView>
        </View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        position: "fixed",
        top: 0,
        left: 0,
    },
    container: {
        flex: 1,
    },
    body: {
        justifyContent: "flex-start",
    },
    bodyPadding: {
        padding: 16,
    },
});
